import React, { useState, useEffect } from 'react';
import {
  Form, Input, Button, Upload, Row, Col, DatePicker, InputNumber, Switch, message
} from 'antd';
import {
  UploadOutlined, SaveOutlined, CloseOutlined, InboxOutlined
} from '@ant-design/icons';
import { createPressRelease, updatePressRelease } from '../../../api/pressReleaseApi';
import moment from 'moment';

const { TextArea } = Input;

const PressReleaseForm = ({ pressRelease, onSubmit, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    if (pressRelease) {
      // Transform file data for Upload component
      if (pressRelease.imageUrl) {
        // Use relative path to work with proxy
        const imageUrl = pressRelease.imageUrl.startsWith('http') 
          ? pressRelease.imageUrl 
          : pressRelease.imageUrl;
        
        setFileList([{
          uid: '-1',
          name: 'Current Image',
          status: 'done',
          url: imageUrl
        }]);
        setPreviewUrl(imageUrl);
      }

      form.setFieldsValue({
        title: pressRelease.title || '',
        description: pressRelease.description || '',
        newspaper: pressRelease.newspaper || '',
        date: pressRelease.date ? moment(pressRelease.date) : moment(),
        link: pressRelease.link || '#',
        order: pressRelease.order || 0,
        isActive: pressRelease.isActive !== undefined ? pressRelease.isActive : true,
      });
    } else {
      form.resetFields();
      setFileList([]);
      setPreviewUrl('');
    }
  }, [pressRelease, form]);

  // Custom upload handler to prevent auto-upload
  const customRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  // Handle file upload changes
  const handleUploadChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    if (newFileList.length > 0 && newFileList[0].originFileObj) {
      const url = URL.createObjectURL(newFileList[0].originFileObj);
      setPreviewUrl(url);
    } else if (newFileList.length > 0 && newFileList[0].url) {
      setPreviewUrl(newFileList[0].url);
    } else {
      setPreviewUrl('');
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const submitData = new FormData();
      
      // Append all form fields
      submitData.append('title', values.title);
      submitData.append('description', values.description || '');
      submitData.append('newspaper', values.newspaper);
      submitData.append('date', values.date ? values.date.toISOString() : moment().toISOString());
      submitData.append('link', values.link || '#');
      submitData.append('order', values.order || 0);
      submitData.append('isActive', values.isActive);

      // Append image file if new file is uploaded
      if (fileList.length > 0 && fileList[0].originFileObj) {
        submitData.append('image', fileList[0].originFileObj);
      } else if (pressRelease?.imageUrl) {
        submitData.append('imageUrl', pressRelease.imageUrl);
      }

      if (pressRelease) {
        await updatePressRelease(pressRelease._id, submitData);
        message.success('Press release updated successfully');
      } else {
        await createPressRelease(submitData);
        message.success('Press release created successfully');
      }

      form.resetFields();
      setFileList([]);
      setPreviewUrl('');
      onSubmit();
    } catch (error) {
      console.error('Error saving press release:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save press release';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Shared style object
  const formStyle = {
    fontFamily: 'Rubik, sans-serif',
    fontSize: '16px',
    fontWeight: 400,
    color: '#000000'
  };

  return (
    <div style={{
      fontFamily: 'Rubik, sans-serif',
    }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          isActive: true,
          order: 0,
          date: moment(),
          link: '#'
        }}
        style={formStyle}
      >
        <Row gutter={16}>
          {/* Title */}
          <Col span={24}>
            <Form.Item
              label={<span style={formStyle}>Title</span>}
              name="title"
              rules={[
                { required: true, message: 'Please enter the title' },
                  { max: 200, message: 'Title cannot exceed 200 characters' }
                ]}
              >
                <Input 
                  placeholder="Enter press release title" 
                  style={formStyle}
                />
              </Form.Item>
            </Col>

            {/* Description */}
            <Col span={24}>
              <Form.Item
                label={<span style={formStyle}>Description</span>}
                name="description"
                rules={[
                  { required: true, message: 'Please enter the description' },
                  { max: 1000, message: 'Description cannot exceed 1000 characters' }
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Enter press release description"
                  showCount
                  maxLength={1000}
                  style={formStyle}
                />
              </Form.Item>
            </Col>

            {/* Newspaper Name */}
            <Col xs={24} md={12}>
              <Form.Item
                label={<span style={formStyle}>Newspaper Name</span>}
                name="newspaper"
                rules={[
                  { required: true, message: 'Please enter the newspaper name' },
                  { max: 100, message: 'Newspaper name cannot exceed 100 characters' }
                ]}
              >
                <Input 
                  placeholder="e.g., The Times of India" 
                  style={formStyle}
                />
              </Form.Item>
            </Col>

            {/* Date */}
            <Col xs={24} md={12}>
              <Form.Item
                label={<span style={formStyle}>Publication Date</span>}
                name="date"
                rules={[{ required: true, message: 'Please select the date' }]}
              >
                <DatePicker 
                  style={{ width: '100%', ...formStyle }}
                  format="MMMM DD, YYYY"
                />
              </Form.Item>
            </Col>

            {/* Image Upload */}
            <Col span={24}>
              <Form.Item
                label={<span style={formStyle}>Image</span>}
                rules={[{ required: !pressRelease, message: 'Please upload an image' }]}
              >
                <Upload
                  listType="picture-card"
                  fileList={fileList}
                  onChange={handleUploadChange}
                  customRequest={customRequest}
                  beforeUpload={(file) => {
                    const isImage = file.type.startsWith('image/');
                    if (!isImage) {
                      message.error('You can only upload image files!');
                    }
                    const isLt5M = file.size / 1024 / 1024 < 5;
                    if (!isLt5M) {
                      message.error('Image must be smaller than 5MB!');
                    }
                    return isImage && isLt5M;
                  }}
                  maxCount={1}
                >
                  {fileList.length === 0 && (
                    <div>
                      <InboxOutlined style={{ fontSize: 40, color: '#470172' }} />
                      <div style={{ marginTop: 8, ...formStyle }}>Upload Image</div>
                    </div>
                  )}
                </Upload>
                {previewUrl && (
                  <div style={{ marginTop: 16 }}>
                    <img 
                      src={previewUrl} 
                      alt="Preview" 
                      style={{ 
                        width: '100%', 
                        maxWidth: '400px', 
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                      }} 
                    />
                  </div>
                )}
              </Form.Item>
            </Col>

            {/* Link */}
            <Col xs={24} md={16}>
              <Form.Item
                label={<span style={formStyle}>External Link (Optional)</span>}
                name="link"
              >
                <Input 
                  placeholder="https://example.com/article" 
                  style={formStyle}
                />
              </Form.Item>
            </Col>

            {/* Order */}
            <Col xs={12} md={4}>
              <Form.Item
                label={<span style={formStyle}>Order</span>}
                name="order"
                tooltip="Display order (lower numbers appear first)"
              >
                <InputNumber 
                  min={0} 
                  style={{ width: '100%', ...formStyle }}
                />
              </Form.Item>
            </Col>

            {/* Active Status */}
            <Col xs={12} md={4}>
              <Form.Item
                label={<span style={formStyle}>Active</span>}
                name="isActive"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          {/* Action Buttons */}
          <Row justify="end" gutter={8}>
            <Col>
              <Button
                onClick={onCancel}
                icon={<CloseOutlined />}
                style={formStyle}
              >
                Cancel
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
                style={{
                  ...formStyle,
                  backgroundColor: '#470172',
                  borderColor: '#470172',
                  color: '#ffffff'
                }}
              >
                {pressRelease ? 'Update' : 'Create'}
              </Button>
            </Col>
          </Row>
        </Form>
    </div>
  );
};

export default PressReleaseForm;
